import React from 'react';
import { Badge, Button, Card, Input, Select, Space, Table, Tag, Typography } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DownloadOutlined, PlusOutlined } from '@ant-design/icons';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { EnterprisePageHeader, WorkspaceLayout } from '@/components/enterprise';
import { useDepartmentsForSelection } from '@/hooks/useDepartments';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { hrmsStatusColor } from '../shared/hrmsStatus';
import { useRecruitList } from './shared/useRecruitList';
import RecruitPeoplePicker from './shared/RecruitPeoplePicker';
import { exportRows, fmtDate } from './shared/recruitUi';

const { Text } = Typography;
/** Unpublishing returns a job to Draft, so there is no separate "Unpublished" status. */
const STATUS_OPTIONS = ['Draft', 'Published', 'Closed'];
const FILTERS = ['status', 'channel', 'department_id', 'recruiter_employee_id', 'requisition_id'] as const;

const JobsPage: React.FC = () => {
  const navigate = useNavigate();
  const list = useRecruitList(FILTERS);
  const { data: departments = [] } = useDepartmentsForSelection();
  const { data, isFetching } = useQuery({
    queryKey: hrmsKeys.jobs(list.apiParams),
    queryFn: () => hrmsService.pageRecruitJobs(list.apiParams),
    placeholderData: keepPreviousData,
  });

  const exportAll = async () => {
    const all = await hrmsService.pageRecruitJobs({ ...list.apiParams, page: 1, limit: 5000 });
    await exportRows(
      'job-postings',
      [
        ['job_number', 'Job #'],
        ['title', 'Title'],
        ['department_name', 'Department'],
        ['requisition_number', 'Requisition'],
        ['channel', 'Channel'],
        ['openings', 'Openings'],
        ['filled_count', 'Filled'],
        ['applications', 'Applications'],
        ['awaiting_review', 'Awaiting review'],
        ['recruiter_name', 'Recruiter'],
        ['closing_date', 'Closing date'],
        ['status', 'Status'],
      ],
      all.rows
    );
  };

  const columns: ColumnsType<any> = [
    {
      title: 'Job #',
      dataIndex: 'job_number',
      key: 'job_number',
      width: 130,
      fixed: 'left',
      sorter: true,
      sortOrder: list.sortOrderFor('job_number'),
      render: (v: string, r) => <a onClick={() => navigate(`/hr/jobs/${r.id}`)}>{v}</a>,
    },
    { title: 'Title', dataIndex: 'title', key: 'title', ellipsis: true, sorter: true, sortOrder: list.sortOrderFor('title') },
    { title: 'Department', dataIndex: 'department_name', width: 150, ellipsis: true },
    { title: 'Channel', dataIndex: 'channel', width: 95 },
    { title: 'Filled', key: 'fill', width: 80, render: (_, r) => `${r.filled_count ?? 0}/${r.openings}` },
    {
      title: 'Applications',
      dataIndex: 'applications',
      key: 'applications',
      width: 140,
      sorter: true,
      sortOrder: list.sortOrderFor('applications'),
      render: (v: number, r) => (
        <Space size={6}>
          <a onClick={() => navigate(`/hr/applications?job_posting_id=${r.id}`)}>{v}</a>
          {Number(r.awaiting_review) > 0 && <Badge count={r.awaiting_review} title="Awaiting review" />}
        </Space>
      ),
    },
    { title: 'Recruiter', dataIndex: 'recruiter_name', width: 140, ellipsis: true, render: (v) => v || '—' },
    {
      title: 'Closes',
      dataIndex: 'closing_date',
      key: 'closing_date',
      width: 120,
      sorter: true,
      sortOrder: list.sortOrderFor('closing_date'),
      render: (v, r) =>
        v && r.status === 'Published' && dayjs(v).isBefore(dayjs(), 'day') ? <Text type="danger">{fmtDate(v)}</Text> : fmtDate(v),
    },
    { title: 'Status', dataIndex: 'status', width: 110, render: (s: string) => <Tag color={hrmsStatusColor(s)}>{s}</Tag> },
  ];

  return (
    <WorkspaceLayout
      fullBleed
      header={
        <EnterprisePageHeader
          documentLabel="Job postings"
          subtitle="Internal and careers-portal openings"
          primaryActions={
            <Space>
              <Button icon={<DownloadOutlined />} onClick={exportAll}>
                Export
              </Button>
              <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/hr/jobs/new')}>
                New job
              </Button>
            </Space>
          }
        />
      }
    >
      <Card size="small" style={{ marginBottom: 12 }}>
        <Space wrap>
          <Input.Search allowClear placeholder="Job #, title, location" defaultValue={list.q} onSearch={list.setQ} style={{ width: 240 }} />
          <Select
            allowClear
            placeholder="Status"
            style={{ width: 140 }}
            value={list.filters.status}
            onChange={(v) => list.setFilter('status', v)}
            options={STATUS_OPTIONS.map((v) => ({ value: v, label: v }))}
          />
          <Select
            allowClear
            placeholder="Channel"
            style={{ width: 140 }}
            value={list.filters.channel}
            onChange={(v) => list.setFilter('channel', v)}
            options={['Both', 'Careers', 'Internal'].map((v) => ({ value: v, label: v }))}
          />
          <Select
            allowClear
            showSearch
            optionFilterProp="label"
            placeholder="Department"
            style={{ width: 200 }}
            value={list.filters.department_id}
            onChange={(v) => list.setFilter('department_id', v)}
            options={departments.map((d: any) => ({ value: d.id, label: d.department_name || d.name }))}
          />
          <RecruitPeoplePicker
            placeholder="Recruiter"
            style={{ width: 200 }}
            value={list.filters.recruiter_employee_id}
            onChange={(v) => list.setFilter('recruiter_employee_id', v as string)}
          />
          {list.activeFilterCount > 0 && (
            <Button type="link" onClick={list.reset}>
              Clear filters
            </Button>
          )}
        </Space>
      </Card>
      <Table
        rowKey="id"
        loading={isFetching}
        columns={columns}
        dataSource={data?.rows || []}
        scroll={{ x: 1200 }}
        pagination={list.pagination(data?.total || 0)}
        onChange={list.onTableChange}
      />
    </WorkspaceLayout>
  );
};

export default JobsPage;
